/**
 * Standing water on a yard apron.
 *
 * ── Why this is not a scaled plane ──────────────────────────────────────────
 * `corridorDirector.ts` makes beat 4's wet patch out of `PlaneGeometry(1, 1)`
 * scaled to a box, and `docs/backlog.md` files what it looks like: *"reads as
 * wet concrete and reads as paint — a hard-edged rectangle"*. The yard wants
 * five or six of them, which would make the same defect five times as visible,
 * so it is solved here once (`docs/beat-6-plan.md` rule 4).
 *
 * ── Why the falloff is stepped and not an alpha ramp ────────────────────────
 * The plan asked for an alpha falloff. The toon shader has no transparency at
 * all — no `transparent`, no opacity uniform, no vertex colours — so an alpha
 * edge would mean adding blending, sort order and depth-write policy to the
 * renderer for one prop, in the beat that is already adding a sky, a rain layer
 * and a surface kind.
 *
 * It would also be the wrong answer. This renderer bands everything it draws:
 * `TOON_RAMP` has two thresholds and the whole art direction is that light
 * arrives in steps. A puddle whose edge dissolves smoothly would be the one
 * soft-edged object in a painted frame. So the falloff is three nested rings of
 * decreasing darkness — deep water, shallow water, damp concrete — and it is
 * stepped in exactly the way the light is. The hard edge was never the problem;
 * a hard RECTANGLE was.
 *
 * ── Why the materials carry a polygon offset ────────────────────────────────
 * A puddle lies on the floor, and the asset contract requires min-Y within 1 mm
 * of 0, so it cannot simply be lifted clear. At the yard's far end — 20 m from
 * the camera, with `camera.near` at 0.01 — one depth-buffer step is already
 * over 2 mm, so a 0.5 mm lift z-fights. The offset is what stops that, and it
 * is a render state rather than a position, so the asset still measures as
 * lying on the ground.
 */

import * as THREE from 'three';
import { mulberry32 } from '@/audio/prng';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

/** Outline resolution. Enough for a lobed edge, few enough to stay a prop. */
const SEGMENTS = 24;

/**
 * Ring boundaries as a fraction of the outline radius, from the centre out.
 *
 * Three bands, not five: the toon ramp is going to quantise the lit result
 * anyway, and past three the extra rings land inside one band and cost
 * triangles to draw a distinction the shader then removes.
 */
const RINGS = [0, 0.42, 0.74, 1] as const;

/** Water depth at the centre, in metres. It is a meniscus, not a pool. */
const CENTRE_RISE = 0.005;

/**
 * The rim's height above the floor.
 *
 * Under the 1 mm the origin contract allows, so the asset still measures as
 * sitting on the ground; the polygon offset below is what actually keeps it out
 * of the floor's depth range.
 */
const RIM_RISE = 0.0005;

/** How far the outline may vary from a circle. Above ~0.3 it stops reading as water. */
const LOBE = 0.24;

export interface PuddleOptions {
  /** Mean radius in metres. */
  readonly radius?: number;
  /** Authored per placement so no two puddles in a yard share an outline. */
  readonly seed?: number;
}

/** Deep, shallow, damp. Albedo in sRGB hex, as every colour in this project is. */
const RING_PAINT = [
  { kind: 'wet' as const, colour: 0x33383e },
  { kind: 'wet' as const, colour: 0x424851 },
  { kind: 'mineral' as const, colour: 0x555b62 },
];

function wetMaterial(kind: 'wet' | 'mineral', colour: number): THREE.Material {
  const material = surface(kind, colour);
  // Depth only. The puddle is not moved, it is biased — see the header.
  material.polygonOffset = true;
  material.polygonOffsetFactor = -2;
  material.polygonOffsetUnits = -4;
  return material;
}

/** Height of the water surface at a normalised radius, 0 at the centre, 1 at the rim. */
function rise(t: number): number {
  return RIM_RISE + (CENTRE_RISE - RIM_RISE) * (1 - t * t);
}

export function buildPuddle(options: PuddleOptions = {}): THREE.Group {
  const radius = options.radius ?? 1.4;
  const random = mulberry32(options.seed ?? 11);

  const group = new THREE.Group();
  group.name = 'puddle';

  // The outline is sampled once and every ring is a scaled copy of it, so the
  // bands stay concentric and the lobes line up. Sampling each ring separately
  // would give three unrelated blobs stacked on each other.
  const outline: number[] = [];
  for (let i = 0; i < SEGMENTS; i += 1) outline.push(1 + (random() - 0.5) * 2 * LOBE);
  // Smooth the ring once so neighbouring samples cannot make a spike; water has
  // no corners, and an unsmoothed random radius reads as a cog.
  const smoothed = outline.map((value, index) => {
    const before = outline[(index - 1 + SEGMENTS) % SEGMENTS] ?? value;
    const after = outline[(index + 1) % SEGMENTS] ?? value;
    return (before + value * 2 + after) / 4;
  });

  // Re-centre the outline on its own bounding box before anything is built.
  //
  // A lobed radius is a sum of random numbers and it does not average to zero:
  // the default seed came out 19 mm off-centre in X against a contract that
  // allows 20 mm. That is not a bug in one seed, it is a coin flip taken once
  // per puddle — and the yard places five or six of them, each with its own.
  // Subtracting the measured centre makes the asset centred BY CONSTRUCTION for
  // every seed, which is the difference between a gate that passes and a gate
  // that happens to have passed.
  const rimX: number[] = [];
  const rimZ: number[] = [];
  for (let i = 0; i < SEGMENTS; i += 1) {
    const angle = (i / SEGMENTS) * Math.PI * 2;
    const r = radius * (smoothed[i] ?? 1);
    rimX.push(Math.cos(angle) * r);
    rimZ.push(Math.sin(angle) * r);
  }
  const offsetX = (Math.min(...rimX) + Math.max(...rimX)) / 2;
  const offsetZ = (Math.min(...rimZ) + Math.max(...rimZ)) / 2;

  const at = (index: number, band: number): [number, number, number] => {
    const angle = (index / SEGMENTS) * Math.PI * 2;
    const scale = smoothed[index % SEGMENTS] ?? 1;
    const r = radius * scale * band;
    return [Math.cos(angle) * r - offsetX, rise(band), Math.sin(angle) * r - offsetZ];
  };

  for (let ring = 0; ring < RINGS.length - 1; ring += 1) {
    const inner = RINGS[ring] ?? 0;
    const outer = RINGS[ring + 1] ?? 1;
    const paint = RING_PAINT[ring];
    if (paint === undefined) continue;

    const positions: number[] = [];
    for (let i = 0; i < SEGMENTS; i += 1) {
      const next = (i + 1) % SEGMENTS;
      const outerHere = at(i, outer);
      const outerNext = at(next, outer);
      if (inner === 0) {
        // The middle band is a fan around a single centre vertex, which sits at
        // the RE-CENTRED origin rather than at the outline's polar centre.
        positions.push(-offsetX, rise(0), -offsetZ, ...outerNext, ...outerHere);
        continue;
      }
      const innerHere = at(i, inner);
      const innerNext = at(next, inner);
      positions.push(...innerHere, ...outerNext, ...outerHere);
      positions.push(...innerHere, ...innerNext, ...outerNext);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.computeVertexNormals();
    const mesh = new THREE.Mesh(geometry, wetMaterial(paint.kind, paint.colour));
    mesh.name = `water-band-${String(ring + 1)}`;
    // It receives light and never blocks it: a 5 mm film of water casting a
    // shadow map entry is a cost with nothing on the other side of it.
    mesh.receiveShadow = true;
    group.add(mesh);
  }

  return group;
}

export const puddleAsset: AssetDefinition = {
  id: 'puddle',
  label: 'Puddle',
  description: 'Standing water on a yard apron, banded rather than blended.',
  // Measured from the default outline rather than intended: the lobes make the
  // real extent a little under twice the mean radius.
  footprint: { width: 2.88, height: 0.005, length: 2.66 },
  create: (): THREE.Group => buildPuddle(),
};
